/**
 * Il KIT DEI PARTNER (03/11/2026): i MATERIALI che un partner può mandare ai suoi contatti, ciascuno coi suoi messaggi
 * già scritti. Simone: «un'azienda che deve dare degli strumenti ai propri commerciali per la vendita, quali sono gli
 * strumenti migliori…? … i video che possono essere mandati singolarmente, oppure anche tutta la collana … non deve
 * essere hard coded sul fotovoltaico … in futuro … i capitoli PDF», e «questi template li creiamo noi … nella console».
 *
 * Un materiale è UNA cosa da mandare (l'invito a iscriversi, la serie dei video di un settore, un video, una pagina,
 * più avanti il PDF di un capitolo del catalogo) e porta i suoi messaggi: breve e lungo per WhatsApp, oggetto e testo
 * per l'email. Il link dentro il messaggio è il link d'invito del partner, che tiene il suo codice e porta al materiale
 * (`indirizzoInvito(codice, destinazione)`). Niente nel codice sa di un settore: settore e indirizzo sono DATI.
 *
 * Segnaposti nei messaggi, fra graffe:
 *  - `{link}`: il link d'invito (obbligatorio in ogni messaggio: senza, il messaggio non porterebbe a nessuno);
 *  - `{nome}`: il nome del contatto; se il partner non lo scrive sparisce con lo spazio prima («Ciao {nome},» → «Ciao,»);
 *  - `{mittente}`: il nome di chi manda (il partner, o YumaOS).
 *
 * File puro, senza dipendenze esterne: lo usano il server (`_shared-pkg`), la console e partner.yumaos.com.
 */
import { destinazioneValida } from './codiceInvito';

export const RACCOLTA_MATERIALI = 'platform_partner_materiali';

/** Che cos'è il materiale: decide l'icona e il raggruppamento nell'app del partner. */
export type TipoMateriale = 'iscrizione' | 'serie_video' | 'video' | 'pagina' | 'pdf';
export const TIPI_MATERIALE: readonly TipoMateriale[] = ['iscrizione', 'serie_video', 'video', 'pagina', 'pdf'];

/** I messaggi pronti di un materiale. */
export interface MessaggiDelMateriale {
  whatsapp_breve: string;
  whatsapp_lungo: string;
  email_oggetto: string;
  email_testo: string;
}

export type VersioneMessaggio = 'whatsapp_breve' | 'whatsapp_lungo' | 'email';

/** `platform_partner_materiali/{id}`: lo scrive solo la console (passando dal server). */
export interface MaterialeDoc {
  /** Come lo vede il partner: «Anteprima del preventivo fotovoltaico». */
  nome: string;
  /** Una riga sotto il nome, per scegliere: «5 video, 12 minuti in tutto». */
  descrizione: string;
  tipo: TipoMateriale;
  /** Dove porta il link (`/it/video/fotovoltaico`); `null` solo per l'invito a iscriversi. */
  destinazione: string | null;
  /** Il settore, come chiave libera (`fotovoltaico`, `serramenti`…), per raggruppare; `null` = per tutti i settori. */
  settore: string | null;
  messaggi: MessaggiDelMateriale;
  /** Lingua dei messaggi (ISO a 2 lettere): al partner si propongono i materiali della sua lingua. */
  lingua: string;
  attivo: boolean;
  /** Posizione nell'elenco del partner (prima i numeri bassi). */
  ordine: number;
  created_at?: unknown;
  updated_at?: unknown;
}

/** Ciò che il partner riceve di un materiale. */
export type MaterialePerIlPartner = Pick<MaterialeDoc, 'nome' | 'descrizione' | 'tipo' | 'destinazione' | 'settore' | 'messaggi'> & { id: string };

export const LUNGHEZZE_MATERIALE = { nome: 80, descrizione: 140, oggetto: 150, messaggio: 4000 } as const;

export type ProblemaMateriale =
  | 'nome_mancante'
  | 'tipo_non_valido'
  | 'destinazione_mancante'
  | 'destinazione_non_valida'
  | 'messaggio_mancante'
  | 'link_mancante'
  | 'segnaposto_sconosciuto'
  | 'troppo_lungo';

export interface ProblemaDelMateriale {
  codice: ProblemaMateriale;
  /** Il messaggio a cui si riferisce, se è di un messaggio. */
  campo?: keyof MessaggiDelMateriale;
}

const SEGNAPOSTI = new Set(['link', 'nome', 'mittente']);
const CAMPI_MESSAGGIO: ReadonlyArray<keyof MessaggiDelMateriale> = ['whatsapp_breve', 'whatsapp_lungo', 'email_oggetto', 'email_testo'];
/** I messaggi che portano il link (l'oggetto dell'email no: il link sta nel testo). */
const CON_LINK: ReadonlyArray<keyof MessaggiDelMateriale> = ['whatsapp_breve', 'whatsapp_lungo', 'email_testo'];

/** I problemi di un materiale prima di salvarlo (la console li mostra, il server li rifiuta). Vuoto = a posto. */
export function problemiDelMateriale(
  m: Pick<MaterialeDoc, 'nome' | 'descrizione' | 'tipo' | 'destinazione' | 'messaggi'>,
): ProblemaDelMateriale[] {
  const problemi: ProblemaDelMateriale[] = [];
  if (!m.nome?.trim()) problemi.push({ codice: 'nome_mancante' });
  if (!TIPI_MATERIALE.includes(m.tipo)) problemi.push({ codice: 'tipo_non_valido' });
  if (m.tipo !== 'iscrizione' && m.destinazione == null) problemi.push({ codice: 'destinazione_mancante' });
  if (m.destinazione != null && !destinazioneValida(m.destinazione)) problemi.push({ codice: 'destinazione_non_valida' });
  if ((m.nome ?? '').length > LUNGHEZZE_MATERIALE.nome || (m.descrizione ?? '').length > LUNGHEZZE_MATERIALE.descrizione) {
    problemi.push({ codice: 'troppo_lungo' });
  }
  for (const campo of CAMPI_MESSAGGIO) {
    const testo = m.messaggi?.[campo] ?? '';
    if (!testo.trim()) {
      problemi.push({ codice: 'messaggio_mancante', campo });
      continue;
    }
    if (CON_LINK.includes(campo) && !testo.includes('{link}')) problemi.push({ codice: 'link_mancante', campo });
    if ([...testo.matchAll(/\{([^{}]*)\}/g)].some((x) => !SEGNAPOSTI.has(x[1]))) problemi.push({ codice: 'segnaposto_sconosciuto', campo });
    const massimo = campo === 'email_oggetto' ? LUNGHEZZE_MATERIALE.oggetto : LUNGHEZZE_MATERIALE.messaggio;
    if (testo.length > massimo) problemi.push({ codice: 'troppo_lungo', campo });
  }
  return problemi;
}

export interface ValoriDelMessaggio {
  link: string;
  nome?: string | null;
  mittente: string;
}

/** Riempie i segnaposti. `{nome}` vuoto sparisce con lo spazio prima; gli altri si sostituiscono e basta. */
export function componiMessaggio(testo: string, valori: ValoriDelMessaggio): string {
  const nome = (valori.nome ?? '').trim();
  return testo
    .replace(/( ?)\{nome\}/g, (_tutto, spazio: string) => (nome ? `${spazio}${nome}` : ''))
    .replace(/\{mittente\}/g, valori.mittente)
    .replace(/\{link\}/g, valori.link);
}
